import axios from 'axios';
import { BrokersContext } from 'contexts/Brokers/Brokers';
import { useContext, useState } from 'react';
import {
	generateLinkToUploadCandidates,
	generateLinkToUploadEmployees,
} from 'services/services';
import Analytics from 'helpers/analytics';
import { OriginUploadCsvType } from './ModalUploadCsv';
import { useToast } from '@welbe/web-design-system';

interface UseUploadCsvProps {
	origin: OriginUploadCsvType;
	onClose?: () => void;
	onSuccess?: () => void;
}

const useModalUploadCsv = ({
	onClose,
	onSuccess,
	origin,
}: UseUploadCsvProps) => {
	const { toast } = useToast();
	const [csvFile, setCsvFile] = useState<any>();
	const [hasError, setError] = useState(false);
	const [isLoading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	const { isBroker } = useContext(BrokersContext);

	const resetStates = async () => {
		setError(false);
		setErrorMessage('');
	};

	const initLoading = () => setLoading(true);

	const finishLoading = () => setLoading(false);

	const handleGenericError = () => toast.error('No se pudo hacer el upload');

	const validateEmptyState = () => {
		const textError = 'Ningún archivo seleccionado';

		if (!csvFile) {
			setError(true);
			setErrorMessage(textError);

			throw new Error(textError);
		}
		return true;
	};

	const validateFormat = () => {
		const textError = 'Archivo inválido';

		if (!csvFile.type.includes('csv')) {
			setError(true);
			setErrorMessage(textError);

			throw new Error(textError);
		}

		return csvFile;
	};

	const requestUploadUrl = async () => {
		try {
			let response;
			switch (origin) {
				case 'employees':
					response = await generateLinkToUploadEmployees();
					break;
				case 'jobProfile':
					response = await generateLinkToUploadEmployees();
					break;
				case 'candidates':
					response = await generateLinkToUploadCandidates();
					break;
				default:
					throw new Error('Modal origin not found.');
			}

			return {
				urlRequest: response.data.content.url,
				csvFile,
			};
		} catch (error) {
			handleGenericError();
		}
	};

	const uploadCsv = async (data: any) => {
		const { urlRequest, csvFile } = data;

		try {
			await axios.put(urlRequest, csvFile, {
				headers: { 'Content-Type': csvFile.type },
			});
			Analytics.track('app', 'Upload CSV Successful', {
				isBroker,
				fileUrl: urlRequest,
				origin,
			});

			toast.success('CSV subido con éxito');
			onClose && onClose();
			onSuccess && onSuccess();
		} catch (error) {
			Analytics.track('app', 'Error Upload CSV', {
				isBroker,
				fileUrl: urlRequest,
				origin,
			});
			handleGenericError();
		}
	};

	const handleSubmit = () => {
		resetStates()
			.then(initLoading)
			.then(validateEmptyState)
			.then(validateFormat)
			.then(requestUploadUrl)
			.then(uploadCsv)
			.finally(finishLoading);
	};

	const handleFile = (e: any) => setCsvFile(e);

	const handleDownloadEmployeeExemple = () => {
		window.open(
			'https://welbe-shared-assets.s3.amazonaws.com/company/files/perfilpuestocolaboradores.csv',
		);
	};

	return {
		csvFile,
		hasError,
		isBroker,
		isLoading,
		errorMessage,
		handleFile,
		handleSubmit,
		handleDownloadEmployeeExemple,
	};
};

export default useModalUploadCsv;
